import { Archive, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import PillSelect from "@/components/common/PillSelect";
import { updateClient } from "@/lib/api";
import { ratingOptions } from "@/pages/clients/clientsUtils";

const RATING_PILLS = [
  { value: "none", label: "No rating", pill: "bg-slate-100 text-slate-500 border-slate-200" },
  ...ratingOptions.map((o) => ({ value: o.value, label: `Rating ${o.label}`, pill: o.color })),
];
const STATUS_PILLS = [
  { value: "good", label: "Good standing", pill: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { value: "on_hold", label: "On Hold", pill: "bg-red-100 text-red-700 border-red-300" },
];

const IconBtn = ({ onClick, title, testId, danger, children }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testId}
    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 transition-colors ${danger ? "text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}>
    {children}
  </button>
);

// Customer header card (WO pattern): Cust # + editable name + site/facility subtitle on the left;
// save status, rating pill, status pill and icon-only Archive / Close on the right.
export function ClientHeaderCard({
  formData, setFormData, selectedClient, setSelectedClient, setClients,
  saveStatus, clientSites, handleRatingChange, onArchive, onClose,
}) {
  const primary = clientSites.find((s) => s.is_primary) || clientSites[0];
  const place = [primary?.city, primary?.state].filter(Boolean).join(", ");
  const subtitle = [
    place,
    clientSites.length ? `${clientSites.length} site${clientSites.length === 1 ? "" : "s"}` : "",
    selectedClient?.facility_type,
  ].filter(Boolean).join(" · ");

  const setHold = async (value) => {
    const hold = value === "on_hold";
    setFormData((f) => ({ ...f, payment_hold: hold }));
    try {
      await updateClient(selectedClient.id, { ...selectedClient, payment_hold: hold });
      setSelectedClient((prev) => ({ ...prev, payment_hold: hold }));
      setClients((list) => list.map((c) => (c.id === selectedClient.id ? { ...c, payment_hold: hold } : c)));
    } catch {
      toast.error("Could not update customer status");
    }
  };

  return (
    <div className="space-y-0 text-left" data-testid="client-header-card">
      <h2 className="sr-only">{selectedClient?.name || formData.name || "Customer details"}</h2>
      <div className="rounded-xl border bg-card px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="client-header-card">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg text-slate-500 shrink-0" title="Customer #" data-testid="client-header-number">
              {formData.customer_number || "—"}
            </span>
            <Input
              value={formData.name || ""}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="text-lg font-semibold text-slate-900 border-0 shadow-none px-0 h-8 focus-visible:ring-0 flex-1 min-w-0"
              data-testid="input-detail-name"
            />
          </div>
          <div className="text-xs text-slate-500 truncate" data-testid="client-header-subtitle">{subtitle || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {saveStatus && (
            <span className={`text-xs italic ${saveStatus === "saving" ? "text-slate-400" : "text-emerald-600"}`} data-testid="save-status">
              {saveStatus === "saving" ? "Saving…" : "Saved"}
            </span>
          )}
          <PillSelect value={selectedClient?.rating || "none"} options={RATING_PILLS} testId="rating-detail" title="Customer rating"
            onChange={(v) => handleRatingChange(selectedClient.id, v === "none" ? "" : v)} />
          <PillSelect value={formData.payment_hold ? "on_hold" : "good"} options={STATUS_PILLS} testId="status-detail" title="Account status" onChange={setHold} />
          <IconBtn onClick={onArchive} title="Archive customer" testId="btn-archive-customer" danger><Archive className="w-4 h-4" /></IconBtn>
          {onClose && (
            <button type="button" title="Close" data-testid="btn-close-client-sheet" onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
